import { css } from '@emotion/core';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { memo } from 'react';

import { bp, colors, easing } from '@/styles';
import { fromNow } from '@/utils/dateTimeHelpers';

const styles = {
  a: css`
    pointer-events: none;
  `,
  card: css`
    display: flex;
    background-color: ${colors.white};
    padding: 24px 16px;
    box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
    border-radius: 2px;
    pointer-events: auto;
    transition-timing-function: ${easing.easeInOut};
    transition-duration: 0.3s;
    transition-property: box-shadow, transform;
    margin-left: -16px;
    margin-right: -16px;
    margin-bottom: 16px;
    &:hover {
      box-shadow: 0 11px 12px 0 rgba(35, 32, 32, 0.15);
      transform: translateY(-1px);
    }
    @media ${bp.md} {
      padding: 28px 32px;
      margin-left: initial;
      margin-right: initial;
      margin-bottom: 20px;
      width: 100%;
    }
    @media print {
      box-shadow: none;
    }
  `,
  tagsContainer: css`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 16px;
    > p:not(:last-child) {
      margin-right: 20px;
    }
  `,
  tag: css`
    color: ${colors.secondaryGrayDark};
    font-size: 12px;
    line-height: 20px;
    font-weight: 900;
    height: 23px;
    padding: 1px 14px;
    border-radius: 100px;
    background-color: ${colors.accentGrayUltralight};
  `,
  cardTitle: css`
    color: ${colors.primaryBlue};
    font-size: 2rem;
    line-height: 3.2rem;
    font-weight: 900;
  `,
  summary: css`
    font-size: 1.6rem;
    line-height: 2.8rem;
    color: ${colors.secondaryGrayDark};
    margin-top: 0.8rem;
  `,
  bottomContainer: css`
    display: flex;
    margin-top: 12px;
    align-items: flex-start;
    > p {
      margin-right: 10px;
      font-size: 12px;
      line-height: 20px;
      color: ${colors.secondaryGrey};
    }
    > p:nth-child(2) {
      font-size: 10px;
      line-height: 18px;
    }
  `,
};

const ThreadListItem = memo(props => {
  const {
    truncate,
    summaryLimit,
    as,
    href,
    Title,
    DateUpdated,
    Details,
    States,
    TopicTags,
    Comments,
  } = props;

  const renderSummary = () => {
    const detailStr = Details.replace(/(<([^>]+)>)/gi, '');
    if (!truncate) return detailStr;
    return (detailStr || '').length > summaryLimit
      ? `${detailStr.substring(0, summaryLimit)}...`
      : detailStr;
  };

  const tags = [...(States || []), ...(TopicTags || [])];
  const commentsCount = Comments.length;
  const moreComments = commentsCount > 1 ? 's' : '';

  return (
    <Link href={href} as={as} passHref>
      <a css={styles.card}>
        <div>
          <div css={styles.tagsContainer}>
            {tags.map(tag => {
              return (
                <p key={tag.Value} css={styles.tag}>
                  {tag.Text}
                </p>
              );
            })}
          </div>
          <p css={styles.cardTitle}>{Title}</p>
          <p
            css={styles.summary}
            dangerouslySetInnerHTML={{
              __html: renderSummary(),
            }}
          />
          <div css={styles.bottomContainer}>
            <p>Published {fromNow(DateUpdated)}</p>
            <p>•</p>
            <p>
              {commentsCount} Comment{moreComments}
            </p>
          </div>
        </div>
      </a>
    </Link>
  );
});

ThreadListItem.propTypes = {
  truncate: PropTypes.bool,
  href: PropTypes.string,
  Title: PropTypes.string.isRequired,
  Details: PropTypes.string,
  summaryLimit: PropTypes.number,
};

ThreadListItem.defaultProps = {
  href: '',
  truncate: true,
  Details: '',
  summaryLimit: 140,
};

export default ThreadListItem;
